import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  blockerAccess,
  reportSourceKey,
  reportText,
  type TaskReportView,
} from '../lib/task-report';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { MarkdownWidget } from './widgets/MarkdownWidget';

// What one run DELIVERED, rendered. PRESENTER — the read is `useTaskReport` in
// `<DevopsReport/>` (routes/).
//
// THE BODY IS UNTRUSTED. It is markdown a run on someone's box wrote, so it goes
// through the one sanitize-then-innerHTML seam — `<MarkdownWidget/>` over
// `renderMarkdown` — and never near a raw `innerHTML` of its own. Same posture as
// the KB page panel, for the same reason.
//
// `report: null` IS A STATE, NOT AN ERROR: a task that has delivered nothing
// answers 200 with a null report, so this renders an empty state rather than an
// error box with a Retry that would answer the same thing for ever.
//
// And it is TWO states, not one. `report_missing` says the run finished, produced
// no deliverable and failed for it — terminal, nothing is coming. "No report YET"
// on that page contradicts the failure pill three lines above it and invites a
// reader to keep refreshing, so the terminal case gets its own copy.
//
// The three sibling blocks are rendered from what they can be PROVEN to carry —
// `reportText` takes a field only when it really is a non-empty string, and
// `blockerAccess` answers a VERDICT so an unreadable `required_access` says so on
// the page instead of rendering as a shorter ask. Their shapes belong to the
// runner lane that emits them, apps never import apps, and a hand-copied mirror
// here would be a second schema to drift out of sync with the one producer.

/** The blocker's ask when the WHOLE list read cleanly; null when there is none to show. */
function accessList(blocker: Record<string, unknown>): readonly string[] | null {
  const verdict = blockerAccess(blocker);
  return verdict.kind === 'list' ? verdict.needs : null;
}

/** The blocker named an ask this app cannot read. Its own line, never a shorter list. */
function accessMalformed(blocker: Record<string, unknown>): boolean {
  return blockerAccess(blocker).kind === 'malformed';
}

/** Which empty state the body panel shows: "not yet", or "the run failed for it". */
function emptyMessage(view: TaskReportView): string {
  return view.report_missing ? t('devops_report.no_report_missing') : t('devops_report.no_report');
}

export function DevopsReportView(props: { readonly view: TaskReportView }): JSX.Element {
  const filings = (): readonly Record<string, unknown>[] => props.view.raw_tasks_filed;

  return (
    <div class="devops-report">
      {/* Only ever plural, and that is why there is no singular key: the line
          exists to say "this is the LATEST attempt", which one run cannot be. */}
      <Show when={props.view.runs_seen > 1}>
        <p class="devops-report__retried">
          {t('devops_report.retried', { count: String(props.view.runs_seen) })}
        </p>
      </Show>

      <Show when={props.view.report_missing}>
        <StatusPill tone="danger">{t('devops_report.report_missing')}</StatusPill>
      </Show>

      <Panel title={t('devops_report.report_title')} class="devops-report__body">
        <Show when={props.view.report} fallback={<EmptyState message={emptyMessage(props.view)} />}>
          {(report) => (
            <>
              <p class="devops-report__source">
                {t('devops_report.source_label')} {t(reportSourceKey(report().source))}
              </p>
              <MarkdownWidget content={report().markdown} />
            </>
          )}
        </Show>
      </Panel>

      <Panel title={t('devops_report.artifacts_title')}>
        <Show
          when={(props.view.report?.artifacts ?? []).length > 0}
          fallback={<EmptyState message={t('devops_report.artifacts_empty')} />}
        >
          <ul class="devops-report__artifacts">
            <For each={props.view.report?.artifacts ?? []}>
              {(artifact) => (
                <li class="devops-report__artifact">
                  <span class="devops-report__artifact-name">{artifact.name}</span>
                  <code class="devops-report__artifact-ref">{artifact.ref}</code>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Panel>

      <Panel title={t('devops_report.filed_title')}>
        <Show
          when={filings().length > 0}
          fallback={<EmptyState message={t('devops_report.filed_empty')} />}
        >
          <ul class="devops-report__filings">
            <For each={filings()}>
              {(filing) => (
                <li class="devops-report__filing">
                  {reportText(filing, 'ask') ?? JSON.stringify(filing)}
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Panel>

      <Show when={props.view.blocker}>
        {(blocker) => (
          <Panel title={t('devops_report.blocker_title')}>
            <p class="devops-report__blocker">
              {reportText(blocker(), 'reason') ?? JSON.stringify(blocker())}
            </p>
            <Show when={accessMalformed(blocker())}>
              <p class="devops-report__access-malformed">{t('devops_report.access_malformed')}</p>
            </Show>
            <Show when={accessList(blocker())}>
              {(needs) => (
                <ul class="devops-report__access">
                  <For each={needs()}>
                    {(need) => (
                      <li>
                        <code>{need}</code>
                      </li>
                    )}
                  </For>
                </ul>
              )}
            </Show>
          </Panel>
        )}
      </Show>

      <Show when={props.view.devops}>
        {(summary) => (
          <Panel title={t('devops_report.summary_title')}>
            <pre class="devops-report__summary">{JSON.stringify(summary(), null, 2)}</pre>
          </Panel>
        )}
      </Show>
    </div>
  );
}
